import axios from 'axios';

/**
 * Helpers for turning a failed API call into something a user can act on.
 *
 * The API writes plain-text bodies (`CustomFunctionResponse.CreateResponse`), e.g.
 * `Email 'x@y.com' already exists` for a 409. Axios does not put that in `error.message` - it only
 * ever says "Request failed with status code 409" - so the body has to be read explicitly.
 */

/** HTTP status of a failed API call, or `undefined` for network/timeout errors. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

/** The message the API sent back, or `fallback` when there is nothing usable. */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(error)) {
    const fromBody = readResponseBody(error.response?.data);
    if (fromBody) return fromBody;

    if (!error.response) {
      return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
        ? 'The request timed out. Please try again.'
        : 'Could not reach the server. Please check your connection and try again.';
    }

    return fallback;
  }

  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * The message to show when a create/update/delete fails.
 *
 * Covers the one case `getApiErrorMessage` cannot: the API authorises each write
 * separately from the route that renders the page, and refuses an unauthorised
 * one with a bare 403 - no body, so there is nothing to read back.
 */
export function getWriteErrorMessage(error: unknown, fallback: string): string {
  return getApiErrorStatus(error) === 403
    ? 'You do not have permission to make this change.'
    : getApiErrorMessage(error, fallback);
}

function readResponseBody(data: unknown): string | null {
  if (typeof data === 'string') {
    const text = data.trim();
    // Ignore HTML error pages from gateways/proxies - never useful to show.
    return text && !text.startsWith('<') ? text : null;
  }

  // Validation failures come back as an array of one-key objects, e.g.
  // `[{ error: "CargoSize is required." }, { error: "Laytime is required." }]`.
  // Join them so the user sees every field that needs fixing, not just the first.
  if (Array.isArray(data)) {
    const messages = data.map(readResponseBody).filter((message): message is string => !!message);
    return messages.length ? messages.join(' ') : null;
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ['message', 'detail', 'title', 'error'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }

  return null;
}
